import { Routes } from '@angular/router';
import { ROUTE_TRANSLATE_KEYS } from '@core/router/route.translate-keys';
import { RouteEntity } from '@core/router/router.entity';
import { authGuard, deniedForAuthorizedGuard } from '@feature/auth/guards/auth-guard';
import { settingsGuard } from '@feature/auth/guards/settings-guard';
import { CouponState } from '@feature/coupon/state/coupon.state';
import { HistoryState } from '@feature/history/state/history.state';
import { MapState } from '@feature/map/state/map.state';
import { TariffsGeneratorService } from '@feature/products/services/tariffs-generator.service';
import { ProductsState } from '@feature/products/state/products.state';
import { DesignElementsService } from '@feature/tickets/services/design-elements.service';
import { TopUpState } from '@feature/top-up/state/topup.state';
import { HeaderComponent } from '@shared/components/layout/header/header.component';


export const routes: Routes = [
    {
        path: RouteEntity.Empty,
        loadComponent: () =>
            import('@shared/components/layout/main-layout/main-layout').then((c) => c.MainLayout),
        children: [
            { path: RouteEntity.Empty, outlet: 'header', component: HeaderComponent },
            {
                path: RouteEntity.Empty,
                redirectTo: RouteEntity.Tickets,
                pathMatch: 'full',
            },
            {
                path: RouteEntity.Login,
                loadComponent: () =>
                    import('@feature/auth/components/login/login').then((c) => c.Login),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Login],
                },
                canActivate: [deniedForAuthorizedGuard],
            },
            {
                path: RouteEntity.Register,
                loadChildren: () =>
                    import('@feature/auth/components/register/register.routes').then((r) => r.routes),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Register],
                },
                canActivate: [deniedForAuthorizedGuard],
            },
            {
                path: RouteEntity.Recover,
                loadChildren: () =>
                    import('@feature/auth/components/recover/recover.routes').then((r) => r.routes),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Recover],
                },
                canActivate: [deniedForAuthorizedGuard],
            },
            {
                path: RouteEntity.AuthCallback,
                loadComponent: () =>
                    import('@feature/auth/components/auth-callback/auth-callback.component').then((c) => c.AuthCallbackComponent),
                canActivate: [deniedForAuthorizedGuard],
            },
            {
                path: RouteEntity.Tickets,
                loadChildren: () =>
                    import('@feature/tickets/tickets.routes').then((r) => r.routes),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Tickets],
                },
                canActivate: [authGuard],
                providers: [DesignElementsService],
            },
            {
                path: RouteEntity.Topup,
                loadChildren: () =>
                    import('@feature/top-up/topup.routes').then((r) => r.routes),
                canActivate: [authGuard, settingsGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Topup],
                },
                providers: [TopUpState],
            },
            {
                path: RouteEntity.Services,
                loadChildren: () =>
                    import('@feature/products/products.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Services],
                },
                providers: [ProductsState, TariffsGeneratorService],
            },
            {
                path: RouteEntity.History,
                loadChildren: () =>
                    import('@feature/history/history.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.History],
                },
                providers: [HistoryState],
            },
            {
                path: RouteEntity.Person,
                loadChildren: () =>
                    import('@feature/person/person.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Person],
                },
            },
            {
                path: RouteEntity.Map,
                loadChildren: () =>
                    import('@feature/map/map.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Map],
                },
                providers: [MapState],
            },
            {
                path: RouteEntity.Support,
                loadChildren: () =>
                    import('@feature/support/support.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Support],
                },
            },
            {
                path: RouteEntity.Coupon,
                loadChildren: () =>
                    import('@feature/coupon/coupon.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Coupon],
                },
                providers: [CouponState],
            },
            {
                path: RouteEntity.Basket,
                loadChildren: () =>
                    import('@feature/basket/basket.routes').then((r) => r.routes),
                canActivate: [authGuard, settingsGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Basket],
                },
            },
            {
                path: 'payment-web',
                loadChildren: () =>
                    import('@feature/basket/basket.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Basket],
                },
            },
            {
                path: RouteEntity.Medium,
                loadChildren: () =>
                    import('@feature/cards/cards.routes').then((r) => r.routes),
                canActivate: [authGuard],
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Medium],
                },
            },
            {
                path: RouteEntity.PrivacyPolicy,
                loadChildren: () =>
                    import('@feature/privacy-policy/privacy-policy.routes').then((r) => r.routes),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.PrivacyPolicy],
                },
            },
            {
                path: RouteEntity.TermsOfUse,
                loadChildren: () =>
                    import('@feature/terms-of-use/terms-of-use.routes').then((r) => r.routes),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.TermsOfUse],
                },
            },
            {
                path: RouteEntity.Logout,
                canActivate: [authGuard],
                loadComponent: () =>
                    import('@feature/logout/logout').then((c) => c.Logout),
                data: {
                    title: ROUTE_TRANSLATE_KEYS[RouteEntity.Logout],
                },
            },
            {
                path: '**',
                loadComponent: () =>
                    import('@shared/components/layout/not-found/not-found.component').then((c) => c.NotFoundComponent),
            },
        ],
    },
    {
        path: '**',
        redirectTo: RouteEntity.Root,
        pathMatch: 'full',
    },
];
